import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Eye, Users, CalendarDays, ShoppingBag, TrendingUp, ShoppingCart, Trophy, CreditCard, Banknote, Share2 } from "lucide-react";
import { http, formatPrice } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import AdminTopBar from "@/components/AdminTopBar";
import { AttemptsTable } from "@/components/AttemptsTable";

const rankBadge = (rank) =>
  rank === 1
    ? "bg-[#f59e0b] text-white"
    : rank === 2
      ? "bg-[#94a3b8] text-white"
      : rank === 3
        ? "bg-[#b45309] text-white"
        : "bg-slate-100 text-[#475569]";

const ProductRankRow = ({ rank, p, metric, testid }) => (
  <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[#f8fafc] last:border-0" data-testid={testid}>
    <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${rankBadge(rank)}`}>
      {rank}
    </span>
    {p.image_url ? (
      <img src={p.image_url} alt={p.name} className="w-10 h-10 object-contain rounded-sm border border-[#f0f0f0] p-0.5 shrink-0" />
    ) : (
      <div className="w-10 h-10 bg-slate-100 rounded-sm shrink-0" />
    )}
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-[#0f172a] truncate">{p.name}</div>
      <div className="text-xs text-[#64748b]">{metric}</div>
    </div>
  </div>
);

const RankPanel = ({ title, icon: Icon, testid, rows, empty, render }) => (
  <div className="bg-white rounded-sm fk-shadow overflow-hidden" data-testid={testid}>
    <div className="flex items-center gap-2 px-4 py-3 border-b border-[#f0f0f0] bg-[#f8fafc]">
      <Icon className="w-4 h-4 text-[#C62828]" />
      <h2 className="font-head font-bold text-[#0f172a] text-base">{title}</h2>
    </div>
    {rows.length ? (
      rows.map(render)
    ) : (
      <div className="px-4 py-10 text-center text-sm text-[#64748b]" data-testid={`${testid}-empty`}>{empty}</div>
    )}
  </div>
);

const AdminAnalytics = () => {
  const { token, isAdmin } = useAdmin();
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [codAttempts, setCodAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");
  const [applied, setApplied] = useState({ start: "", end: "" });

  const fetchAnalytics = (range) => {
    setLoading(true);
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    http
      .get("/admin/analytics", { headers: { Authorization: `Bearer ${token}` }, params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchAnalytics(applied);
    http
      .get("/admin/payment-attempts", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAttempts(r.data.attempts))
      .catch(() => {});
    http
      .get("/admin/cod-attempts", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setCodAttempts(r.data.attempts))
      .catch(() => {});
    // eslint-disable-next-line
  }, [isAdmin, applied]);

  const applyRange = () => {
    if (startInput && endInput && startInput > endInput) return;
    setApplied({ start: startInput, end: endInput });
  };

  const resetRange = () => {
    setStartInput("");
    setEndInput("");
    setApplied({ start: "", end: "" });
  };

  if (!isAdmin) return <Navigate to="/admin/login" replace />;

  const maxVisits = data ? Math.max(1, ...data.daily.map((d) => d.visits)) : 1;

  const cards = data
    ? [
        { label: "Total Website Visits", value: data.total_visits, icon: Eye, testid: "stat-total-visits" },
        { label: "Unique Visitors", value: data.unique_visitors, icon: Users, testid: "stat-unique-visitors" },
        { label: "Visits Today", value: data.visits_today, icon: CalendarDays, testid: "stat-visits-today" },
        { label: "Units Sold", value: data.total_units_sold, icon: ShoppingBag, testid: "stat-units-sold" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-[#f1f5f9]" data-testid="admin-analytics-page">
      <AdminTopBar />
      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="font-head text-xl font-bold text-[#0f172a]">Store Analytics</h1>
          <div className="flex flex-wrap items-center gap-2" data-testid="date-range-controls">
            <div className="flex items-center gap-1.5 bg-white border border-[#e2e8f0] rounded-sm px-2 h-9">
              <span className="text-xs text-[#64748b] font-medium">From</span>
              <input
                type="date"
                value={startInput}
                max={endInput || undefined}
                onChange={(e) => setStartInput(e.target.value)}
                className="text-sm text-[#0f172a] outline-none bg-transparent"
                data-testid="date-from-input"
              />
            </div>
            <div className="flex items-center gap-1.5 bg-white border border-[#e2e8f0] rounded-sm px-2 h-9">
              <span className="text-xs text-[#64748b] font-medium">To</span>
              <input
                type="date"
                value={endInput}
                min={startInput || undefined}
                onChange={(e) => setEndInput(e.target.value)}
                className="text-sm text-[#0f172a] outline-none bg-transparent"
                data-testid="date-to-input"
              />
            </div>
            <button
              onClick={applyRange}
              className="bg-[#C62828] text-white text-sm font-semibold px-4 h-9 rounded-sm hover:bg-[#A01F1F]"
              data-testid="date-apply-btn"
            >
              Apply
            </button>
            {(applied.start || applied.end) && (
              <button
                onClick={resetRange}
                className="text-sm font-medium text-[#64748b] border border-[#e2e8f0] px-3 h-9 rounded-sm hover:bg-slate-50"
                data-testid="date-reset-btn"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {data && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#64748b]" data-testid="range-indicator">
              {applied.start || applied.end
                ? `Showing ${applied.start || "…"} → ${applied.end || "…"}`
                : "Showing all time"}
            </span>
            <span className="text-sm text-[#64748b]" data-testid="stat-total-revenue">
              {applied.start || applied.end ? "Revenue" : "Total revenue"}:{" "}
              <span className="font-semibold text-[#15803d]">{formatPrice(data.total_revenue)}</span>
            </span>
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-[#64748b]">Loading analytics…</div>
        ) : !data ? (
          <div className="py-20 text-center text-[#64748b]" data-testid="analytics-error">Could not load analytics.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {cards.map((c) => (
                <div key={c.testid} className="bg-white rounded-sm fk-shadow px-4 py-4 flex items-center gap-3" data-testid={c.testid}>
                  <div className="w-10 h-10 rounded-sm bg-[#FCEBEA] flex items-center justify-center shrink-0">
                    <c.icon className="w-5 h-5 text-[#C62828]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#0f172a] leading-none">{c.value}</div>
                    <div className="text-xs text-[#64748b] mt-1">{c.label}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-sm fk-shadow" data-testid="visits-chart">
              <div className="px-4 py-3 border-b border-[#f0f0f0] bg-[#f8fafc]">
                <h2 className="font-head font-bold text-[#0f172a] text-base" data-testid="visits-chart-title">
                  {applied.start || applied.end ? "Visits — Selected Range" : "Visits — Last 7 Days"}
                </h2>
              </div>
              <div className="flex items-end gap-1 sm:gap-2 h-40 px-4 pt-4 pb-2 overflow-x-auto">
                {data.daily.map((d, i) => (
                  <div key={d.date} className="flex-1 min-w-[20px] flex flex-col items-center justify-end gap-1 h-full" data-testid={`chart-bar-${d.date}`}>
                    <span className="text-[10px] font-medium text-[#475569]">{d.visits}</span>
                    <div
                      className="w-full max-w-[48px] bg-[#C62828]/85 rounded-t-sm transition-[height] duration-500"
                      style={{ height: `${(d.visits / maxVisits) * 100}%`, minHeight: d.visits ? 4 : 2 }}
                    />
                    <span className="text-[10px] text-[#64748b]">
                      {data.daily.length > 15 && i % Math.ceil(data.daily.length / 8) !== 0 ? "" : d.date.slice(5)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <RankPanel
                title="Most Viewed Products"
                icon={TrendingUp}
                testid="table-most-viewed"
                rows={data.top_visited}
                empty="No product views tracked yet."
                render={(p, i) => (
                  <ProductRankRow key={p.id} rank={i + 1} p={p} metric={`${p.views} views`} testid={`most-viewed-row-${i}`} />
                )}
              />
              <RankPanel
                title="Added to Cart, Not Ordered"
                icon={ShoppingCart}
                testid="table-abandoned"
                rows={data.abandoned}
                empty="No cart additions tracked yet."
                render={(p, i) => (
                  <ProductRankRow
                    key={p.id}
                    rank={i + 1}
                    p={p}
                    metric={`Added ${p.added}× · Ordered ${p.ordered}× · Not ordered ${p.not_ordered}×`}
                    testid={`abandoned-row-${i}`}
                  />
                )}
              />
              <RankPanel
                title="Top Selling Products"
                icon={Trophy}
                testid="table-top-selling"
                rows={data.top_selling}
                empty="No sales yet."
                render={(p, i) => (
                  <ProductRankRow
                    key={p.id}
                    rank={i + 1}
                    p={p}
                    metric={`${p.units} sold · ${formatPrice(p.revenue)}`}
                    testid={`top-selling-row-${i}`}
                  />
                )}
              />
            </div>

            <RankPanel
              title={`Most Shared Products · ${data.total_shares} shares · ${data.total_share_visits} visits from shares`}
              icon={Share2}
              testid="table-most-shared"
              rows={data.top_shared}
              empty="No products shared yet."
              render={(p, i) => (
                <ProductRankRow
                  key={p.id}
                  rank={i + 1}
                  p={p}
                  metric={`${p.shares} shares (WhatsApp ${p.whatsapp} · Copy link ${p.copy}) · ${p.share_visits} visits from shares`}
                  testid={`most-shared-row-${i}`}
                />
              )}
            />

            <AttemptsTable title="Payment Attempts" icon={CreditCard} rows={attempts}
              testid="table-payment-attempts" rowPrefix="payment-attempt-row" empty="No payment attempts yet." />
            <AttemptsTable title="COD Attempts" icon={Banknote} rows={codAttempts}
              testid="table-cod-attempts" rowPrefix="cod-attempt-row" empty="No COD attempts yet." />
          </>
        )}
      </main>
    </div>
  );
};

export default AdminAnalytics;
